import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/ui/Layout';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RevenueKPICards from './components/RevenueKPICards';
import RevenueChart from './components/RevenueChart';
import CommissionBreakdown from './components/CommissionBreakdown';
import TopRevenuePartners from './components/TopRevenuePartners';
import PaymentMethodDistribution from './components/PaymentMethodDistribution';
import FinancialDetailsTable from './components/FinancialDetailsTable';

const RevenueAnalytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current-month');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedView, setSelectedView] = useState('revenue');

  const periods = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'current-month', label: 'Current Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'current-quarter', label: 'Current Quarter' },
    { value: 'current-year', label: 'Current Year' },
  ];

  const currencies = [
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  ];

  const views = [
    { value: 'revenue', label: 'Revenue View', icon: 'IndianRupee' },
    { value: 'profit-loss', label: 'P&L View', icon: 'TrendingUp' },
    { value: 'forecast', label: 'Forecast View', icon: 'BarChart3' },
  ];

  const handleRefresh = async () => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const handleExport = async () => {
    // Simulate export functionality
    await new Promise((resolve) => setTimeout(resolve, 2000));
  };

  const handleFilter = () => {
    // Handle filter functionality
    console.log('Filter clicked');
  };

  const customActions = [
    {
      id: 'forecast',
      label: 'Generate Forecast',
      icon: 'TrendingUp',
      onClick: () => console.log('Generate forecast'),
      variant: 'default',
    },
    {
      id: 'compare',
      label: 'Compare Periods',
      icon: 'GitCompare',
      onClick: () => console.log('Compare periods'),
      variant: 'outline',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Revenue Analytics - Qkly Admin Panel</title>
        <meta
          name='description'
          content='Comprehensive financial intelligence and revenue monitoring across all business verticals'
        />
      </Helmet>
      <Layout>
        <div className='px-6 py-8'>
          {/* Header Section */}
          <div className='mb-8'>
            <Breadcrumb className='mb-4' />

            <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0'>
              <div>
                <h1 className='text-3xl font-bold text-foreground mb-2'>
                  Revenue Analytics
                </h1>
                <p className='text-muted-foreground'>
                  Comprehensive financial intelligence and revenue monitoring
                  across all business verticals
                </p>
              </div>

              <QuickActionToolbar
                actions={customActions}
                onRefresh={handleRefresh}
                onExport={handleExport}
                onFilter={handleFilter}
                className='flex-shrink-0'
              />
            </div>
          </div>

          {/* Control Panel */}
          <div className='bg-card border border-border rounded-lg p-6 mb-8 elevation-1'>
            <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0'>
              <div className='flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4'>
                {/* Period Selector */}
                <div className='flex items-center space-x-2'>
                  <Icon
                    name='Calendar'
                    size={16}
                    className='text-muted-foreground'
                  />
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e?.target?.value)}
                    className='px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary'
                  >
                    {periods?.map((period) => (
                      <option key={period?.value} value={period?.value}>
                        {period?.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Currency Toggle */}
                <div className='flex items-center space-x-2'>
                  <Icon
                    name='IndianRupee'
                    size={16}
                    className='text-muted-foreground'
                  />
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e?.target?.value)}
                    className='px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary'
                  >
                    {currencies?.map((currency) => (
                      <option key={currency?.value} value={currency?.value}>
                        {currency?.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* View Switcher */}
              <div className='flex space-x-2'>
                {views?.map((view) => (
                  <Button
                    key={view?.value}
                    variant={
                      selectedView === view?.value ? 'default' : 'outline'
                    }
                    size='sm'
                    onClick={() => setSelectedView(view?.value)}
                    iconName={view?.icon}
                    iconPosition='left'
                  >
                    {view?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <RevenueKPICards />

          {/* Main Content Grid */}
          <div className='grid grid-cols-1 xl:grid-cols-12 gap-8 mb-8'>
            {/* Main Chart Area - 8 columns */}
            <div className='xl:col-span-8'>
              <RevenueChart />
            </div>

            {/* Side Panel - 4 columns */}
            <div className='xl:col-span-4 space-y-6'>
              <CommissionBreakdown />
              <TopRevenuePartners />
            </div>
          </div>

          {/* Secondary Content Grid */}
          <div className='grid grid-cols-1 xl:grid-cols-12 gap-8 mb-8'>
            {/* Payment Distribution - 4 columns */}
            <div className='xl:col-span-4'>
              <PaymentMethodDistribution />
            </div>

            {/* Additional Metrics - 8 columns */}
            <div className='xl:col-span-8'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Revenue Forecast Card */}
                <div className='bg-card border border-border rounded-lg p-6 elevation-1'>
                  <div className='flex items-center justify-between mb-4'>
                    <div>
                      <h3 className='text-lg font-semibold text-foreground mb-1'>
                        Revenue Forecast
                      </h3>
                      <p className='text-sm text-muted-foreground'>
                        Next 30 days projection
                      </p>
                    </div>
                    <Icon
                      name='TrendingUp'
                      size={20}
                      className='text-success'
                    />
                  </div>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm text-muted-foreground'>
                        Projected Revenue
                      </span>
                      <span className='text-lg font-bold text-foreground'>
                        $3,124,500
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm text-muted-foreground'>
                        Growth Rate
                      </span>
                      <span className='text-sm font-medium text-success'>
                        +9.7%
                      </span>
                    </div>
                    <div className='w-full bg-muted rounded-full h-2'>
                      <div
                        className='bg-success h-2 rounded-full'
                        style={{ width: '73%' }}
                      ></div>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      73% confidence level
                    </p>
                  </div>
                </div>

                {/* Dispute Impact Card */}
                <div className='bg-card border border-border rounded-lg p-6 elevation-1'>
                  <div className='flex items-center justify-between mb-4'>
                    <div>
                      <h3 className='text-lg font-semibold text-foreground mb-1'>
                        Dispute Impact
                      </h3>
                      <p className='text-sm text-muted-foreground'>
                        Revenue affected by disputes
                      </p>
                    </div>
                    <Icon
                      name='AlertTriangle'
                      size={20}
                      className='text-warning'
                    />
                  </div>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm text-muted-foreground'>
                        Disputed Amount
                      </span>
                      <span className='text-lg font-bold text-error'>
                        $45,670
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm text-muted-foreground'>
                        Active Disputes
                      </span>
                      <span className='text-sm font-medium text-warning'>
                        12
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm text-muted-foreground'>
                        Resolution Rate
                      </span>
                      <span className='text-sm font-medium text-success'>
                        87%
                      </span>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      className='w-full'
                      iconName='MessageSquare'
                    >
                      Manage Disputes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Details Table */}
          <FinancialDetailsTable />
        </div>
      </Layout>
    </>
  );
};

export default RevenueAnalytics;
